const STORAGE_KEY = "dive_connection";
const SESSION_VERIFIER_KEY = "dive_oauth_verifier";
const SESSION_STATE_KEY = "dive_oauth_state";
const SESSION_DIVE_URL_KEY = "dive_oauth_url";
const PENDING_TASKS_KEY = "dive_pending_tasks";

interface PendingTask {
  title: string;
  firstSteps?: string[];
  queuedAt: number;
}

export function getPendingDiveTasks(): PendingTask[] {
  try { return JSON.parse(localStorage.getItem(PENDING_TASKS_KEY) ?? "[]"); }
  catch { return []; }
}

export function queueDiveTask(title: string, firstSteps?: string[]): void {
  const pending = getPendingDiveTasks();
  pending.push({ title, firstSteps, queuedAt: Date.now() });
  localStorage.setItem(PENDING_TASKS_KEY, JSON.stringify(pending));
}

export async function flushPendingDiveTasks(connection: DiveConnection): Promise<number> {
  const pending = getPendingDiveTasks();
  if (pending.length === 0) return 0;
  let sent = 0;
  const remaining: PendingTask[] = [];
  for (const task of pending) {
    try {
      await sendToDive(connection, task.title, task.firstSteps);
      sent++;
    } catch {
      remaining.push(task);
    }
  }
  localStorage.setItem(PENDING_TASKS_KEY, JSON.stringify(remaining));
  return sent;
}

export interface DiveConnection {
  diveUrl: string;
  diveToken: string;
}

export function getDiveConnection(): DiveConnection | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as DiveConnection;
  } catch {
    return null;
  }
}

export function saveDiveConnection(diveUrl: string, diveToken: string): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ diveUrl, diveToken }));
}

export function clearDiveConnection(): void {
  localStorage.removeItem(STORAGE_KEY);
}

// PKCE helpers

function base64urlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let str = "";
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

function generateVerifier(): string {
  const bytes = new Uint8Array(48);
  crypto.getRandomValues(bytes);
  return base64urlEncode(bytes.buffer);
}

async function computeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return base64urlEncode(hash);
}

export async function initiateDiveOAuth(diveUrl: string): Promise<void> {
  const verifier = generateVerifier();
  const challenge = await computeChallenge(verifier);
  const state = base64urlEncode(crypto.getRandomValues(new Uint8Array(16)).buffer);
  const redirectUri = window.location.origin + "/connect";

  sessionStorage.setItem(SESSION_VERIFIER_KEY, verifier);
  sessionStorage.setItem(SESSION_STATE_KEY, state);
  sessionStorage.setItem(SESSION_DIVE_URL_KEY, diveUrl);

  const params = new URLSearchParams({
    client_id: "crux",
    redirect_uri: redirectUri,
    response_type: "code",
    scope: "tasks:write",
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });

  window.location.href = `${diveUrl}/oauth/authorize?${params}`;
}

export async function exchangeOAuthCode(code: string, state: string): Promise<DiveConnection> {
  const savedState = sessionStorage.getItem(SESSION_STATE_KEY);
  const verifier = sessionStorage.getItem(SESSION_VERIFIER_KEY);
  const diveUrl = sessionStorage.getItem(SESSION_DIVE_URL_KEY);

  if (!savedState || state !== savedState) throw new Error("Invalid state — possible CSRF");
  if (!verifier || !diveUrl) throw new Error("Missing OAuth session data");

  const redirectUri = window.location.origin + "/connect";

  const res = await fetch(`${diveUrl}/oauth/token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      grant_type: "authorization_code",
      code,
      code_verifier: verifier,
      client_id: "crux",
      redirect_uri: redirectUri,
    }),
  });

  sessionStorage.removeItem(SESSION_VERIFIER_KEY);
  sessionStorage.removeItem(SESSION_STATE_KEY);
  sessionStorage.removeItem(SESSION_DIVE_URL_KEY);

  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Token exchange failed: ${body}`);
  }

  const { access_token } = (await res.json()) as { access_token: string };
  saveDiveConnection(diveUrl, access_token);
  return { diveUrl, diveToken: access_token };
}

export async function sendToDive(
  connection: DiveConnection,
  title: string,
  firstSteps?: string[],
): Promise<void> {
  const res = await fetch(`${connection.diveUrl}/api/tasks/ingest`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${connection.diveToken}`,
    },
    body: JSON.stringify({ title, firstSteps }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to send task to Dive: ${text}`);
  }
}
